/**
 * Chart Tooltip Helpers
 *
 * Shared tooltip builders for Google Charts with consistent styling.
 */

// Stacked area/bar colors (matches chart color palette)
const CHART_COLORS = [
    '#4eb5ac', '#6bc4ba', '#85d0c9', '#a6dad5',
    '#3da59d', '#2d8b84', '#b2f5ea', '#98D8C8', '#7cc9be'
];

// Contributions color for value composition charts
const CONTRIBUTIONS_COLOR = '#e2e8f0';

// Distributions color for value composition charts
const DISTRIBUTIONS_COLOR = '#94a3b8';

// Maximum number of items to show before collapsing
const MAX_VISIBLE_ITEMS = 8;

// Maximum tooltip height before scrolling
const MAX_TOOLTIP_HEIGHT = '300px';

/**
 * Build HTML tooltip for stacked time series chart
 * Shows date, each share class with value, and total
 * Handles overflow by showing scrollable list or collapsing small values
 *
 * @param {Array} row - Data row [timestamp, value1, value2, ...]
 * @param {Array} classes - Array of share class keys
 * @param {Object} clientData - Client data with equity config
 * @returns {string} HTML tooltip string
 */
export function buildStackedTimeSeriestooltip(row, classes, clientData) {
    let timestamp = Number(row[0]);
    let date = new Date(timestamp).toLocaleDateString("en-US", {
        year: 'numeric', month: 'short', day: 'numeric'
    });

    // Build array of items with values for sorting/filtering
    // Skip classes with 0 holdings to keep tooltip clean
    let items = [];
    let totalValue = 0;

    for (let i = 0; i < classes.length; i++) {
        let value = row[i + 1] || 0;
        totalValue += value;

        // Skip classes with zero value - user has no holdings in this class
        if (value === 0) continue;

        let displayName = clientData.equity?.[classes[i]]?.name || classes[i];
        let color = CHART_COLORS[i % CHART_COLORS.length];
        items.push({ name: displayName, value: value, color: color });
    }

    // Sort by value descending so largest values show first
    items.sort((a, b) => b.value - a.value);

    // Build rows, collapsing if too many items
    let classRows = '';
    let hiddenCount = 0;
    let hiddenValue = 0;

    items.forEach((item, index) => {
        if (index < MAX_VISIBLE_ITEMS) {
            let formattedValue = "$" + Math.round(item.value).toLocaleString();
            classRows += `<tr style="line-height: 16px;">
                <td style="padding: 2px 6px 2px 0; white-space: nowrap;">
                    <span style="display: inline-block; width: 10px; height: 10px; background: ${item.color}; border-radius: 2px; margin-right: 6px;"></span>
                    ${item.name}
                </td>
                <td style="padding: 2px 0; text-align: right; white-space: nowrap;">${formattedValue}</td>
            </tr>`;
        } else {
            hiddenCount++;
            hiddenValue += item.value;
        }
    });

    // Add "and X more" row if items were hidden
    if (hiddenCount > 0) {
        let formattedHiddenValue = "$" + Math.round(hiddenValue).toLocaleString();
        classRows += `<tr style="line-height: 16px; color: #666;">
            <td style="padding: 2px 6px 2px 0; white-space: nowrap; font-style: italic;">
                <span style="display: inline-block; width: 10px; height: 10px; background: #ccc; border-radius: 2px; margin-right: 6px;"></span>
                +${hiddenCount} more
            </td>
            <td style="padding: 2px 0; text-align: right; white-space: nowrap; font-style: italic;">${formattedHiddenValue}</td>
        </tr>`;
    }

    let formattedTotal = "$" + Math.round(totalValue).toLocaleString();

    // Use max-height with overflow for scrolling if needed
    let tableStyle = items.length > MAX_VISIBLE_ITEMS
        ? `border-collapse: collapse; width: 100%;`
        : `border-collapse: collapse; width: 100%;`;

    return `<div style="padding: 10px; font-size: 13px; min-width: 200px; max-width: 320px; white-space: nowrap;">
        <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">${date}</div>
        <div style="max-height: ${MAX_TOOLTIP_HEIGHT}; overflow-y: auto;">
            <table style="${tableStyle}">
                ${classRows}
            </table>
        </div>
        <table style="border-collapse: collapse; width: 100%; margin-top: 4px;">
            <tr style="line-height: 16px; border-top: 1px solid #ddd;">
                <td style="padding: 6px 6px 2px 0; font-weight: bold; white-space: nowrap;">Total Value</td>
                <td style="padding: 6px 0 2px 0; text-align: right; font-weight: bold; white-space: nowrap;">${formattedTotal}</td>
            </tr>
        </table>
    </div>`;
}

/**
 * Build HTML tooltip for value composition bar chart
 * Shows category label, each equity class value with breakdown (for Total Value bar), and total
 * Each class shows an indented breakdown of Capital Account, Distributions, and RoC (if non-zero)
 * Only shows breakdown details for "Total Value" category, not "Contributions"
 *
 * @param {string} category - Category label ("Contributions" or "Total Value")
 * @param {Object} values - Object with { contributions, classes: [{name, value, capitalAccount?, distributions?, returnsOfCapital?}] }
 * @returns {string} HTML tooltip string
 */
export function buildValueCompositionTooltip(category, values) {
    // For Contributions bar, show simple tooltip with just the total
    const isContributionsBar = category === 'Contributions';

    if (isContributionsBar) {
        let totalContributions = values.contributions || 0;
        let formattedTotal = "$" + Math.round(totalContributions).toLocaleString();

        return `<div style="padding: 10px; font-size: 13px; min-width: 160px; white-space: nowrap;">
            <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">${category}</div>
            <table style="border-collapse: collapse; width: 100%;">
                <tr style="line-height: 16px;">
                    <td style="padding: 2px 6px 2px 0; white-space: nowrap;">
                        <span style="display: inline-block; width: 10px; height: 10px; background: ${CONTRIBUTIONS_COLOR}; border-radius: 2px; margin-right: 6px;"></span>
                        Total Contributed
                    </td>
                    <td style="padding: 2px 0; text-align: right; white-space: nowrap;">${formattedTotal}</td>
                </tr>
            </table>
        </div>`;
    }

    // For Total Value bar, show full breakdown with class details
    let items = [];
    let totalValue = 0;

    // Add each equity class with breakdown (include negative values so total matches headline)
    if (values.classes && values.classes.length > 0) {
        values.classes.forEach((cls, i) => {
            if (cls.value && cls.value !== 0) {
                totalValue += cls.value;
                items.push({
                    name: cls.name,
                    value: cls.value,
                    color: CHART_COLORS[i % CHART_COLORS.length],
                    // Include breakdown details if available
                    capitalAccount: cls.capitalAccount,
                    distributions: cls.distributions,
                    returnsOfCapital: cls.returnsOfCapital
                });
            }
        });
    }

    // Sort items by value descending
    items.sort((a, b) => b.value - a.value);

    // Build rows - show all items (scrollable container handles overflow)
    let classRows = '';

    items.forEach((item, index) => {
        let formattedValue = "$" + Math.round(item.value).toLocaleString();
        classRows += `<tr style="line-height: 18px;">
            <td style="padding: 3px 8px 3px 0; white-space: nowrap;">
                <span style="display: inline-block; width: 10px; height: 10px; background: ${item.color}; border-radius: 2px; margin-right: 6px;"></span>
                ${item.name}
            </td>
            <td style="padding: 3px 0; text-align: right; white-space: nowrap;">${formattedValue}</td>
        </tr>`;

        // Add indented breakdown for equity classes if breakdown data is available
        if (item.capitalAccount !== undefined || item.distributions !== undefined || item.returnsOfCapital !== undefined) {
            // Only show breakdown items that have non-zero values
            if (item.capitalAccount && item.capitalAccount !== 0) {
                let formattedCapAcct = "$" + Math.round(item.capitalAccount).toLocaleString();
                classRows += `<tr style="line-height: 14px; color: #666; font-size: 11px;">
                    <td style="padding: 1px 6px 1px 22px; white-space: nowrap;">Capital Account</td>
                    <td style="padding: 1px 0; text-align: right; white-space: nowrap;">${formattedCapAcct}</td>
                </tr>`;
            }
            if (item.distributions && item.distributions !== 0) {
                let formattedDist = "$" + Math.round(item.distributions).toLocaleString();
                classRows += `<tr style="line-height: 14px; color: #666; font-size: 11px;">
                    <td style="padding: 1px 6px 1px 22px; white-space: nowrap;">Distributions</td>
                    <td style="padding: 1px 0; text-align: right; white-space: nowrap;">${formattedDist}</td>
                </tr>`;
            }
            if (item.returnsOfCapital && item.returnsOfCapital !== 0) {
                let formattedRoC = "$" + Math.round(item.returnsOfCapital).toLocaleString();
                classRows += `<tr style="line-height: 14px; color: #666; font-size: 11px;">
                    <td style="padding: 1px 6px 1px 22px; white-space: nowrap;">Return of Capital</td>
                    <td style="padding: 1px 0; text-align: right; white-space: nowrap;">${formattedRoC}</td>
                </tr>`;
            }
        }
    });

    let formattedTotal = "$" + Math.round(totalValue).toLocaleString();

    // Use scrollable container with max-height for many classes
    // Wider max-width for clients with long property names (e.g., ACR real estate)
    return `<div style="padding: 10px; font-size: 13px; min-width: 200px; max-width: 450px;">
        <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">${category}</div>
        <div style="max-height: 280px; overflow-y: auto; padding-right: 4px;">
            <table style="border-collapse: collapse; width: 100%;">
                ${classRows}
            </table>
        </div>
        <table style="border-collapse: collapse; width: 100%; margin-top: 4px;">
            <tr style="line-height: 16px; border-top: 1px solid #ddd;">
                <td style="padding: 6px 6px 2px 0; font-weight: bold;">Total Value</td>
                <td style="padding: 6px 0 2px 0; text-align: right; font-weight: bold;">${formattedTotal}</td>
            </tr>
        </table>
    </div>`;
}

/**
 * Build HTML tooltip for single-class time series chart
 * Simplified design: just shows formatted date and value
 *
 * @param {number} timestamp - Data point timestamp (milliseconds)
 * @param {number} value - Data point value
 * @returns {string} HTML tooltip string
 */
export function buildClassTimeSeriestooltip(timestamp, value) {
    let date = new Date(timestamp).toLocaleDateString("en-US", {
        year: 'numeric', month: 'short', day: 'numeric'
    });
    let formattedValue = "$" + Math.round(value).toLocaleString();

    return `<div style="padding: 10px; font-size: 13px; white-space: nowrap;">
        <div style="font-weight: bold; margin-bottom: 4px;">${date}</div>
        <div>${formattedValue}</div>
    </div>`;
}

/**
 * Determine if bar segment annotations should be hidden
 * Returns true if there are too many segments or segments are too small
 *
 * @param {number} numClasses - Number of equity classes
 * @param {number} totalValue - Total bar value
 * @param {number} minSegmentValue - Smallest segment value
 * @returns {boolean} True if annotations should be hidden
 */
export function shouldHideAnnotations(numClasses, totalValue, minSegmentValue) {
    // Hide if more than 5 classes (labels will overlap)
    if (numClasses > 5) return true;

    // Hide if smallest visible segment is less than 5% of total
    if (totalValue > 0 && minSegmentValue / totalValue < 0.05) return true;

    return false;
}

export { CHART_COLORS, CONTRIBUTIONS_COLOR, DISTRIBUTIONS_COLOR };
